import React, { useCallback, useEffect, useState } from 'react';
import { api, formatCOP } from '../services/api';
import { IS_FACTURACION } from '../config';
import { entregaParts, pedidoClienteDisplay } from '../utils/entregaAddress';

const ESTADO_LABEL = IS_FACTURACION
  ? {
      pendiente: 'Por facturar',
      en_proceso: 'Facturando',
      facturado: 'Facturado',
    }
  : {
      confirmado: 'Por preparar',
      en_preparacion: 'Separando',
      entregado: 'Completado',
    };

const FILTROS = IS_FACTURACION
  ? [
      { value: 'pendiente', label: 'Por facturar' },
      { value: 'en_proceso', label: 'Facturando' },
      { value: 'facturado', label: 'Facturados' },
      { value: '', label: 'Todos' },
    ]
  : [
      { value: 'confirmado', label: 'Por preparar' },
      { value: 'en_preparacion', label: 'Separando' },
      { value: 'entregado', label: 'Completados' },
      { value: '', label: 'Todos' },
    ];

const POLL_MS = 60000; // SSE primero; red de seguridad suave (hasta 3 tablets bodega)

function estadoClass(estado) {
  if (IS_FACTURACION) {
    if (estado === 'en_proceso') return 'estado-badge estado-en_preparacion';
    if (estado === 'facturado') return 'estado-badge estado-entregado';
    return 'estado-badge estado-confirmado';
  }
  return `estado-badge estado-${estado}`;
}

function rowEstado(p) {
  if (!IS_FACTURACION) return p.estado;
  const st = String(p.facturacion_estado || '').trim().toLowerCase();
  if (st === 'en_proceso' || st === 'facturado') return st;
  return 'pendiente';
}

function fmtHora(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
}

export default function BodegaInbox({ pin, onSelect, selectedId, refreshKey = 0 }) {
  const [filtro, setFiltro] = useState(IS_FACTURACION ? 'pendiente' : 'confirmado');
  const [q, setQ] = useState('');
  const [pedidos, setPedidos] = useState([]);
  const [cola, setCola] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const loadSeq = React.useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setError('');
    try {
      const res = await api.getPedidos({ estado: filtro || undefined, q: q.trim() || undefined });
      if (seq !== loadSeq.current) return;
      setPedidos(res.data || []);
      setCola(res.cola || null);
    } catch (err) {
      if (seq !== loadSeq.current) return;
      // No vaciar la lista si ya había datos: la app no se “queda ciega” por un fallo momentáneo
      setError(err.message || 'No se pudieron cargar los pedidos');
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [pin, filtro, q]);

  useEffect(() => {
    setLoading(true);
    load();
    const t = setInterval(load, POLL_MS);
    const onVis = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [load]);

  useEffect(() => {
    if (refreshKey > 0) load();
  }, [refreshKey, load]);

  const porPreparar = IS_FACTURACION
    ? cola?.pendiente ?? pedidos.filter((p) => rowEstado(p) === 'pendiente').length
    : cola?.por_preparar ?? pedidos.filter((p) => p.estado === 'confirmado').length;

  return (
    <div className="inbox">
      <header className="panel-head">
        <div>
          <h2>{IS_FACTURACION ? 'Completados' : 'Pedidos'}</h2>
        </div>
        {(IS_FACTURACION ? filtro === 'pendiente' : filtro === 'confirmado') && porPreparar > 0 ? (
          <span className="badge-count">{porPreparar}</span>
        ) : null}
      </header>

      {cola ? (
        <div className="cola-panel">
          {IS_FACTURACION ? (
            <p>
              <strong>{cola.pendiente || 0}</strong> por facturar
              <span className="muted"> · </span>
              <strong>{cola.en_proceso || 0}</strong> facturando
              <span className="muted"> · </span>
              <strong>{cola.facturado || 0}</strong> facturados
            </p>
          ) : (
            <p>
              <strong>{cola.por_preparar || 0}</strong> por preparar
              <span className="muted"> · </span>
              <strong>{cola.preparando || 0}</strong> separando
            </p>
          )}
        </div>
      ) : null}

      <div className="toolbar">
        <input
          type="search"
          placeholder="Buscar número o cliente..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load()}
        />
        <button type="button" onClick={load}>Buscar</button>
        <button type="button" className="btn-ghost" onClick={load} title="Actualizar">
          ↻
        </button>
      </div>

      <div className="filtros">
        {FILTROS.map((f) => (
          <button
            key={f.value || 'all'}
            type="button"
            className={`chip ${filtro === f.value ? 'active' : ''}`}
            onClick={() => setFiltro(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error ? <p className="error banner inbox-error">{error}</p> : null}

      {loading ? (
        <p className="muted center">Cargando pedidos...</p>
      ) : pedidos.length === 0 && !error ? (
        <p className="empty center">
          {filtro === 'confirmado' || filtro === 'pendiente'
            ? IS_FACTURACION
              ? 'No hay pedidos por facturar.'
              : 'No hay pedidos por preparar.'
            : 'No hay pedidos con este filtro.'}
        </p>
      ) : (
        <ul className="pedido-list">
          {pedidos.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                className={`pedido-row ${selectedId === p.id ? 'selected' : ''}`}
                onClick={() => onSelect(p.id)}
              >
                <div className="pedido-row-main">
                  <strong>{p.numero}</strong>
                  <span className={estadoClass(rowEstado(p))}>
                    {ESTADO_LABEL[rowEstado(p)] || rowEstado(p)}
                  </span>
                </div>
                <div className="pedido-row-sub">
                  <span>{pedidoClienteDisplay(p)}</span>
                  {(p.es_sede || p.padre_codigo) ? (
                    <span className="sede-pill">Sucursal</span>
                  ) : null}
                  {p.vendedor ? <span> · {p.vendedor}</span> : null}
                </div>
                {(() => {
                  const e = entregaParts(p);
                  if (!e.direccion && !e.ciudadZona) return null;
                  return (
                    <p className="pedido-row-dir">
                      {e.direccion || e.ciudadZona}
                      {e.direccion && e.ciudadZona ? ` · ${e.ciudadZona}` : ''}
                    </p>
                  );
                })()}
                {p.estado === 'en_preparacion' && p.preparado_por_nombre ? (
                  <p className="pedido-separando">Separando: {p.preparado_por_nombre}</p>
                ) : null}
                {p.estado === 'entregado' && p.preparado_por_nombre ? (
                  <p className="pedido-asignado">
                    {IS_FACTURACION ? `Separado por ${p.preparado_por_nombre}` : `Completado por ${p.preparado_por_nombre}`}
                  </p>
                ) : null}
                {IS_FACTURACION && p.tipo_documento ? (
                  <p className="pedido-asignado">
                    {String(p.tipo_documento).toLowerCase() === 'iva' ? 'Factura' : 'Remisión'}
                    {p.documento_numero ? `: ${p.documento_numero}` : ''}
                  </p>
                ) : null}
                {IS_FACTURACION && p.facturado_por_nombre ? (
                  <p className="pedido-asignado">Factura: {p.facturado_por_nombre}</p>
                ) : null}
                {IS_FACTURACION && (p.facturado_inicio_at || p.facturado_fin_at) ? (
                  <p className="pedido-asignado">
                    {p.facturado_inicio_at ? `Inicio ${fmtHora(p.facturado_inicio_at)}` : ''}
                    {p.facturado_inicio_at && p.facturado_fin_at ? ' · ' : ''}
                    {p.facturado_fin_at ? `Fin ${fmtHora(p.facturado_fin_at)}` : ''}
                  </p>
                ) : null}
                <div className="pedido-row-meta">
                  <span>{formatCOP(p.subtotal)}</span>
                  <span>
                    {new Date(p.created_at).toLocaleString('es-CO', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })}
                  </span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
